'use client'

import { ChangeEvent, useMemo } from 'react'
import { InputSelect } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { BundleDiscountModel } from '@marketplace-web/bundles/items-bundle-data'

type Props = {
  isDisabled: boolean
  discountFractions: Array<string>
  selectedDiscounts: Array<BundleDiscountModel>
  onDiscountSelection: (discounts: BundleDiscountModel) => void
}

const BundleDiscountsSelectionList = ({
  isDisabled,
  discountFractions,
  selectedDiscounts,
  onDiscountSelection,
}: Props) => {
  const translate = useTranslate('settings.bundle_discounts')

  const availableFractionValues = useMemo<
    React.ComponentProps<typeof InputSelect>['options']
  >(() => {
    return discountFractions.map(fraction => {
      const fractionNumeric = Number(fraction)

      return {
        label: fractionNumeric === 0 ? translate('no_discount') : `${fractionNumeric * 100}%`,
        value: fraction,
      }
    })
  }, [translate, discountFractions])

  function getSelectedDropDownValue(discount: BundleDiscountModel) {
    return availableFractionValues.find(fraction => fraction.value === discount.fraction)?.value
  }

  function handleDiscountSelection(discount: BundleDiscountModel) {
    return (event: ChangeEvent<HTMLSelectElement>) => {
      onDiscountSelection({
        ...discount,
        fraction: event.target.value,
      })
    }
  }

  return (
    <>
      {selectedDiscounts.map((discount, index) => (
        <InputSelect
          disabled={isDisabled}
          key={discount.minimalItemCount}
          name={`bundle-discount-selector-${index}`}
          testId={`bundle-discount-selector-${index}`}
          title={translate(
            'items',
            { count: discount.minimalItemCount },
            { count: discount.minimalItemCount },
          )}
          options={availableFractionValues}
          value={getSelectedDropDownValue(discount) ?? ''}
          onChange={handleDiscountSelection(discount)}
        />
      ))}
    </>
  )
}

export default BundleDiscountsSelectionList
